import { useFocusEffect, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { Modal, Platform, Pressable, ScrollView, View } from 'react-native';

import { AdminModeHeader } from '@/components/admin-mode-header';
import {
  ContactCaptureForm,
  type ContactCaptureValues,
} from '@/components/contacts/contact-capture-form';
import { AdminContactsList } from '@/components/contacts/admin-contacts-list';
import { MemberHeaderBar } from '@/components/member-header-bar';
import { captureContact, exportAdminContactsCsv, fetchAdminContacts } from '@/lib/contacts';
import type { ContactWithVenue } from '@sixam/shared/contact-core';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

export default function AdminContactsScreen() {
  const { tenant } = useLocalSearchParams<{ tenant: string }>();
  const contactCaptureEnabled = useReleaseFeatureFlag('contactCapture') === true;
  const [contacts, setContacts] = React.useState<ContactWithVenue[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isExporting, setIsExporting] = React.useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = React.useState(false);
  const [isSavingContact, setIsSavingContact] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [addContactError, setAddContactError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  const loadContacts = React.useCallback(() => {
    let active = true;
    setIsLoading(true);
    fetchAdminContacts(tenant)
      .then((data) => {
        if (active) {
          setContacts(data);
          setError(null);
        }
      })
      .catch((loadError: unknown) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load contacts.');
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [tenant]);

  useFocusEffect(loadContacts);

  async function handleExport() {
    setIsExporting(true);
    setError(null);
    try {
      const csv = await exportAdminContactsCsv(tenant);
      if (Platform.OS === 'web') {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${tenant}-contacts.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : 'Could not export contacts.');
    } finally {
      setIsExporting(false);
    }
  }

  async function handleAddContact(values: ContactCaptureValues) {
    setIsSavingContact(true);
    setAddContactError(null);
    try {
      await captureContact(
        {
          consentStatus: values.marketingConsent ? 'opted_in' : 'unknown',
          email: values.email,
          name: values.name,
          phone: values.phone,
          source: 'admin_manual',
          tenantSlug: tenant,
        },
        { authenticated: true }
      );
      setContacts(await fetchAdminContacts(tenant));
      setIsAddContactOpen(false);
    } catch (contactError) {
      setAddContactError(
        contactError instanceof Error ? contactError.message : 'Could not save contact.'
      );
    } finally {
      setIsSavingContact(false);
    }
  }

  return (
    <View className="flex-1 bg-background" collapsable={false}>
      <MemberHeaderBar
        mode="admin"
        onCreatePress={contactCaptureEnabled ? () => setIsAddContactOpen(true) : undefined}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search contacts"
      />
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        contentContainerClassName="gap-4 p-6 pb-28"
        scrollEventThrottle={16}
      >
        <AdminModeHeader title="Contacts" />
        <AdminContactsList
          contacts={contacts}
          error={error}
          isExporting={isExporting}
          isLoading={isLoading}
          onExport={() => void handleExport()}
          searchQuery={Platform.OS === 'web' ? undefined : searchQuery}
        />
      </ScrollView>
      {contactCaptureEnabled ? (
        <Modal
          animationType="slide"
          onRequestClose={() => setIsAddContactOpen(false)}
          transparent
          visible={isAddContactOpen}
        >
          <Pressable
            className="flex-1 justify-end bg-black/45"
            onPress={() => setIsAddContactOpen(false)}
          >
            <Pressable className="rounded-t-2xl border border-border bg-background p-5">
              <ContactCaptureForm
                error={addContactError}
                isSubmitting={isSavingContact}
                onSubmit={(values) => void handleAddContact(values)}
                submitLabel="Save contact"
              />
            </Pressable>
          </Pressable>
        </Modal>
      ) : null}
    </View>
  );
}
